import React, { useEffect, useState } from 'react'
import { useApplicationStore } from 'hooks/application'
import { useParams, generatePath } from 'react-router-dom'
import api from 'helpers/api'
import { getErrorSupportMetadata } from 'helpers/apiError'
import { IApiKey } from 'types'
import Button from 'components/button'
import { Arrow, Plus } from 'components/icons'
import OrganisationKeyModal from 'views/organisation/components/keyModal'
import Modal from 'components/modal'
import { ORGANISATION_DASHBOARD } from 'routes'

const OrganisationApiKeys: React.FC = () => {
  const params: { organisationId?: string } = useParams()
  const organisationId = params.organisationId

  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()

  const [loadingApiKeys, setLoadingApiKeys] = useState<boolean>(false)
  const [apiKeys, setApiKeys] = useState<IApiKey[]>([])
  const [newKeyModalOpen, setNewKeyModalOpen] = useState<boolean>(false)
  const [editKeyModalOpen, setEditKeyModalOpen] = useState<boolean>(false)
  const [deleteKeyDialogOpen, setDeleteKeyDialogOpen] = useState<boolean>(false)
  const [selectedAPIKeyPublicId, setSelectedAPIKeyPublicId] = useState<string>('')

  const selectedAPIKey = apiKeys
    ?.map((key) => {
      return {
        ...key,
        accessLevel: key.accessLevel === undefined ? 1 : key.accessLevel,
        projectRestrictions: key.projectRestrictions === undefined ? [] : key.projectRestrictions,
        tableRestrictions: key.tableRestrictions === undefined ? [] : key.tableRestrictions,
        documentRestrictions: key.documentRestrictions === undefined ? [] : key.documentRestrictions
      }
    })
    .find((apiKey) => apiKey.publicId === selectedAPIKeyPublicId)

  useEffect(() => {
    document.title = 'Morta | Organisation API Keys'

    if (!params.organisationId) {
      displayErrorMessage(
        "This link doesn't point to an organisation. Open API keys from your organisation's settings."
      )
    }

    setLoadingApiKeys(true)
    api
      .getOrganisationApiKeys(params.organisationId!)
      .then((response) => {
        setApiKeys(response.data)
      })
      .catch((error) => {
        const support = getErrorSupportMetadata(error)
        if (!support.offerSupport) {
          displayErrorMessage(error)
          return
        }
        setSnackbarMessage({
          status: 'error',
          message: "Your organisation's API keys couldn't be loaded. Refresh the page to try again.",
          ...support
        })
      })
      .finally(() => {
        setLoadingApiKeys(false)
      })
  }, [organisationId])

  const deleteAPIKey = async () => {
    if (!organisationId || !selectedAPIKey) return

    const keyPublicId = selectedAPIKey.publicId
    const filteredKeys = apiKeys.filter((key) => key.publicId !== keyPublicId)
    try {
      await api.deleteOrganisationApiKey(organisationId, keyPublicId)
      setApiKeys(filteredKeys)
    } catch (error) {
      displayErrorMessage(error)
    }

    setDeleteKeyDialogOpen(false)
  }

  if (!organisationId) {
    return <div className="p-5">Organisation ID is missing from URL</div>
  }

  return (
    <div className="w-full py-0 px-[30px]">
      <div className="flex flex-row items-center w-full text-lg font-bold mt-2.5 mb-8 py-2.5">
        <Button to={generatePath(ORGANISATION_DASHBOARD, { organisationId })} internalType="outline">
          <Arrow className="mr-[5px]" type="left" /> Back To Organisation Dashboard
        </Button>
        <div className="ml-auto">
          <Button to="https://docs.morta.io" internalType="secondary">
            API Docs
          </Button>
        </div>
        <Button
          className="ml-[7px]"
          data-cy="new-api-key"
          onClick={() => setNewKeyModalOpen(true)}
          internalType="accept"
        >
          <Plus className="mr-[5px]" />
          API Key
        </Button>
      </div>

      {loadingApiKeys && <div>Loading API keys...</div>}

      {!loadingApiKeys && apiKeys.length === 0 && (
        <div>
          <div>Your organisation has no API keys</div>
        </div>
      )}

      {!loadingApiKeys &&
        apiKeys.length > 0 &&
        apiKeys.map(function (apiKey: any) {
          return (
            <div
              key={apiKey.publicId}
              className="flex flex-row items-center rounded shadow-heavy w-full p-2.5 flex-wrap my-5 border border-divider-strong"
            >
              <div>
                <span className="font-bold">{apiKey.name}</span> (starts with {apiKey.prefix})
              </div>
              <Button
                className="mr-2.5 ml-auto max-w-[150px]"
                onClick={() => {
                  setEditKeyModalOpen(true)
                  setSelectedAPIKeyPublicId(apiKey.publicId)
                }}
                data-cy="edit-api-key"
              >
                Edit
              </Button>
              <Button
                className="mr-2.5 max-w-[150px]"
                internalType="danger"
                onClick={() => {
                  setDeleteKeyDialogOpen(true)
                  setSelectedAPIKeyPublicId(apiKey.publicId)
                }}
                data-cy="delete-api-key"
              >
                Delete
              </Button>
            </div>
          )
        })}

      {newKeyModalOpen && (
        <OrganisationKeyModal
          id="org-new-api-key-modal"
          open={newKeyModalOpen}
          setOpen={setNewKeyModalOpen}
          organisationId={organisationId}
          mode="new"
          initialApiKey={{
            name: 'New API Key',
            accessLevel: 1,
            siteRestrictions: [],
            projectRestrictions: [],
            tableRestrictions: [],
            documentRestrictions: [],
            hash: '',
            prefix: '',
            publicId: ''
          }}
          onCreated={(key) => {
            setApiKeys((prev) => [...prev, key])
          }}
        />
      )}

      {editKeyModalOpen && selectedAPIKey && (
        <OrganisationKeyModal
          id="org-edit-api-key-modal"
          open={editKeyModalOpen}
          setOpen={setEditKeyModalOpen}
          organisationId={organisationId}
          mode="edit"
          initialApiKey={selectedAPIKey}
          onUpdated={(updated) => {
            setApiKeys((prev) => prev.map((k) => (k.publicId === updated.publicId ? updated : k)))
          }}
          onClose={() => setSelectedAPIKeyPublicId('')}
        />
      )}

      {selectedAPIKey && (
        <Modal
          id="org-delete-api-key-modal"
          open={deleteKeyDialogOpen}
          setOpen={setDeleteKeyDialogOpen}
          title="Delete API Key"
        >
          Once you have deleted this API key it cannot be restored.
          <div className="flex items-center justify-end mt-5">
            <Button internalType="danger" onClick={() => setDeleteKeyDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              className="ml-2.5"
              internalType="accept"
              onClick={deleteAPIKey}
              data-cy="org-confirm-delete-api-key"
            >
              Confirm
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default OrganisationApiKeys
